<template>
  <!-- Default content -->
  <div v-if="!selectedAgreement" class="flex min-h-screen justify-center">
    <vs-card class="rounded-none lg:rounded-lg m-0 lg:m-10 contract-agreement-card">
      <div class="h-full flex flex-col justify-between text-base">
        <div class="p-4 lg:p-10">
          <div class="mb-12 flex lg:block justify-between items-center">
            <div class="relative lg:fixed top-0 left-0 m-0 lg:mx-8 lg:my-10">
              <SelectOrganization />
            </div>
            <h4 class="lg:text-center font-semibold hidden sm:block">
              Contracts Summary
            </h4>
            <vs-button
              @click="logout"
              type="border"
              color="primary"
              icon-pack="feather"
              icon="icon-log-out"
              :icon-after="true"
              class="relative lg:fixed top-0 right-0 m-0 lg:mx-8 lg:my-10 shadow-md px-4 py-2 lg:py-3 font-semibold bg-white"
            >
              Logout
            </vs-button>
          </div>
          <div class="mb-12 flex justify-between items-center sm:hidden">
            <h4 class="lg:text-center font-semibold">
              Contracts Summary
            </h4>
          </div>
          <h1 class="font-semibold text-2xl mb-2">
            Fresh Clinics Contracts Summary
          </h1>
          <p class="text-tag-cancelled-text mb-6">
            To continue accessing the Fresh Clinics platform, please review and accept the updated Clinic Agreement, Terms & Conditions, and End User Licence Agreement (EULA) provided below.
          </p>
          <p class="text-tag-cancelled-text mb-12">
            These documents govern your use, as well as the use of any clinics you own or manage, and outline the rights and obligations of all parties.
          </p>
          <div class="flex flex-col gap-6">
            <div
              v-for="(agreement, index) in agreements"
              :key="index"
              :class="[agreement.content && agreement.content != '' ? 'cursor-pointer' : 'cursor-not-allowed', 'flex items-center gap-3']"
              @click="handleAgreementClick(agreement)"
            >
              <span :class="[agreement.status ? 'bg-primary' : '', 'inline-flex p-1 rounded border border-solid border-gray-500']">
                <vs-icon
                  icon-pack="feather"
                  :icon="agreement.status ? 'icon-check' : 'icon-x'"
                  color="white"
                  size="small"
                >
                </vs-icon>
              </span>
              <span :class="[agreement.content && agreement.content != '' ? 'text-primary' : 'text-gray-500', 'underline ']">
                {{ agreement.label }}
              </span>
            </div>
          </div>
        </div>
        <div class="bg-white p-4 lg:p-10 flex flex-col lg:flex-row gap-4 lg:gap-0 justify-between items-center sticky bottom-0 left-0 right-0">
          <span :class="[allAgreementsAccepted ? 'opacity-0' : '','text-blue-900']">
            Please read through each agreement to accept and continue.
          </span>
          <vs-button
            :disabled="!allAgreementsAccepted"
            class="w-full lg:w-auto"
            @click="saveAgreements"
          >
            Continue
          </vs-button>
        </div>
      </div>
    </vs-card>
  </div>
  <!-- Content for selected agreement -->
  <div v-else class="flex flex-col justify-between h-screen bg-white">
    <div class="flex flex-col items-center overflow-auto" ref="agreementContent" @scroll="checkScroll">
      <div class="max-w-lg">
        <div class="sticky bg-white top-0 left-0 right-0 lg:static p-4 lg:pt-16">
          <h4 class="text-center font-semibold text-2xl mb-0 lg:mb-4">
            {{ selectedAgreement.label }}
          </h4>
          <vs-button
            class="contract-agreement-content-back-btn"
            @click="closeModal"
            type="flat"
            icon-pack="feather"
            icon="icon-x"
            size="large"
          ></vs-button>
        </div>
        <div class="p-4 lg:py-16 lg:pt-4">
          <div>
            <div v-html="selectedAgreement.content"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-white sticky bottom-0 left-0 right-0 shadow-lg">
      <div class="max-w-lg p-4 lg:p-10 flex flex-col lg:flex-row gap-4 lg:gap-6 justify-center items-center mx-auto">
        <span class="text-blue-900">
          Please scroll and read through the agreements to continue.
        </span>
        <vs-button
          :disabled="!selectedAgreement.scrolledToBottom"
          class="w-full lg:w-auto"
          @click="acceptAgreement"
        >
          Accept
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
  import firebase from 'firebase/app';
  import 'firebase/auth';
  import axios from '@/axios';
  import SelectOrganization from './SelectOrganization.vue';

  export default {
    name: 'ContractAgreement',
    components: {
      SelectOrganization,
    },
    data() {
      return {
        agreements: [
          {
            key: 'clinicAgreement',
            label: '...',
            status: false,
            scrolledToBottom: false,
            content: '',
          },
          {
            key: 'termsConditions',
            label: '...',
            status: false,
            scrolledToBottom: false,
            content: '',
          },
          {
            key: 'eulaAgreement',
            label: '...',
            status: false,
            scrolledToBottom: false,
            content: '',
          },
        ],
        selectedAgreement: null,
      };
    },
    methods: {
      cleanupOrphanedLoaders() {
        this.$nextTick(() => {
          document.querySelectorAll('.con-vs-loading').forEach((loader) => loader.remove());
        });
      },
      async getAgreementLogByClinicId() {
        try {
          this.$vs.loading();

          const organizationId = this.getLocalStorageItem('OrganizationId');
          const token = this.getLocalStorageItem('AccessToken');
          const authHeader = { Authorization: token };
          let clinicId;

          // Fetch clinics under the organization
          try {
            const clinicsData = await axios.get(`/api/v1/organization/${organizationId}/clinics/list`, {
              headers: authHeader,
            });

            clinicId = this.findKeyValue(clinicsData.data, 'clinicId');

            if (!clinicId){
              this.$vs.loading.close();
              this.$vs.notify({
                title: 'No Clinics Found',
                text: 'This organization has no clinics. Please contact the administrator.',
                color: 'warning',
              });
              return;
            }
          } catch (error) {
            this.$vs.loading.close();
            this.$vs.notify({
              title: 'Error',
              text: 'Unable to fetch clinics. Please try again later.',
              color: 'danger',
            });
            return;
          }

          const { data } = await axios.get(`/api/v2/agreement-logs?clinicId=${clinicId}`, { headers: authHeader });

          // Dynamically map slugs to agreements
          this.agreements = [
            {
              key: 'clinicAgreement',
              label:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.agreement_page_slug
                ).title || '',
              status: data.data.clinicAgreement,
              scrolledToBottom: false,
              content:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.agreement_page_slug
                ).content || '',
            },
            {
              key: 'termsConditions',
              label:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.terms_page_slug
                ).title || '',
              status: data.data.termsConditions,
              scrolledToBottom: false,
              content:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.terms_page_slug
                ).content || '',
            },
            {
              key: 'eulaAgreement',
              label:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.eula_page_slug
                ).title || '',
              status: data.data.eulaAgreement,
              scrolledToBottom: false,
              content:
                data.data.agreement.find(
                  (item) => item.slug === data.data.slugs.eula_page_slug
                ).content || '',
            },
          ];
        } catch (error) {
          console.error('Error fetching agreements:', error);
        } finally {
          this.$vs.loading.close();
        }
      },
      getLocalStorageItem(key) {
        return localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}${key}`);
      },
      handleAgreementClick(agreement) {
        if (agreement.content && agreement.content !== '') {
          this.selectAgreement(agreement);
        } else {
          this.$vs.notify({
            title: 'Page Not Found',
            text: `The page for "${agreement.label}" is not found. Please contact the administrator.`,
            color: 'warning',
          });
        }
      },
      selectAgreement(agreement) {
        if (agreement.content) {
          this.selectedAgreement = agreement;

          this.$nextTick(() => {
            this.checkScroll();
          });
        }
      },
      closeModal() {
        this.selectedAgreement = null;
        this.cleanupOrphanedLoaders();
      },
      logout() {
        const firebaseCurrentUser = firebase.auth().currentUser;

        if (firebaseCurrentUser) {
          firebase
            .auth()
            .signOut()
            .then(() => {
              this.$router.push('/login').catch(() => {});
            });
        }

        // If JWT login
        if (this.getLocalStorageItem('AccessToken')) {
          sessionStorage.removeItem('doctorClinicSelectedClinic');
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}AccessToken`
          );
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}FirstName`
          );
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}TokenExpiry`
          );
          localStorage.removeItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`);
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}LoggedIn`
          );
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}RefreshToken`
          );

          this.$cookies.remove(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}RefreshToken`
          );
          localStorage.removeItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}Role`);
          localStorage.removeItem(
            `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
          );

          window.location = '/login';
        }

        // Change role on logout. Same value as initialRole of acj.js
        this.$acl.change('admin');
        localStorage.removeItem('userInfo');

        // This is just for demo Purpose. If user clicks on logout -> redirect
        window.location = '/login';
      },
      checkScroll() {
        const content = this.$refs.agreementContent;
        if (content && this.selectedAgreement) {
          // Check if the content requires scrolling
          if (content.scrollHeight > content.clientHeight) {
            // Content requires scrolling, check if user scrolled to the bottom
            if (
              content.scrollTop + content.clientHeight >= content.scrollHeight
            ) {
              this.selectedAgreement.scrolledToBottom = true;
            }
          } else {
            // No scrolling required, mark as scrolled to bottom
            this.selectedAgreement.scrolledToBottom = true;
          }
        }
      },
      acceptAgreement() {
        if (this.selectedAgreement) {
          this.selectedAgreement.status = true;
          this.selectedAgreement = null;
          this.cleanupOrphanedLoaders();
        }
      },
      saveAgreements() {
        this.$vs.loading();

        const organizationId = localStorage.getItem(
          `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`
        );

        const user = JSON.parse(localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`));

        const token = localStorage.getItem(
          `${process.env.VUE_APP_ACCESS_TOKEN_NAME}AccessToken`
        );

        const authHeader = { Authorization: `Bearer ${token}` };

        const payload = {
          orgId: organizationId,
          termsConditions: this.agreements.find(a => a.key === 'termsConditions').status,
          clinicAgreement: this.agreements.find(a => a.key === 'clinicAgreement').status,
          eulaAgreement: this.agreements.find(a => a.key === 'eulaAgreement').status,
          platform: /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'Mobile' : 'Web',
          deviceType: /Android/i.test(navigator.userAgent)
            ? 'Android'
            : /iPhone|iPad|iPod/i.test(navigator.userAgent)
            ? 'IOS'
            : 'Web',
        };

        axios
          .post('/api/v2/agreement-logs', payload, { headers: authHeader })
          .then(() => {
            this.cleanupOrphanedLoaders();

            this.$vs.notify({
              title: 'Success',
              text: 'Agreements saved successfully. Redirecting to Dashboard...',
              color: 'success',
            });

            this.$router.push(`/${user && user.userType.toUpperCase() === 'NURSE' ? 'nurse' : 'org-owner'}/home`);
          })
          .catch((error) => {
            console.error('Error saving agreements:', error);
            this.cleanupOrphanedLoaders();

            this.$vs.notify({
              title: 'Error',
              text: 'Failed to save agreements. Please try again.',
              color: 'danger',
            });
          });
      },
      findKeyValue(obj, key) {
        if (!obj || typeof obj !== 'object') return undefined;

        // If the current level is an array, iterate through its elements
        if (Array.isArray(obj)) {
          for (const item of obj) {
            const value = this.findKeyValue(item, key);
            if (value !== undefined) {
              return value;
            }
          }
        } else {
          // If the current level is an object, check for the key and iterate through its properties
          if (obj.hasOwnProperty(key)) {
            return obj[key];
          }

          for (const k in obj) {
            if (obj.hasOwnProperty(k)) {
              const value = this.findKeyValue(obj[k], key);
              if (value !== undefined) {
                return value;
              }
            }
          }
        }

        return undefined;
      },
    },
    computed: {
      allAgreementsAccepted() {
        return this.agreements.every((agreement) => agreement.status);
      },
    },
    async mounted() {
      this.cleanupOrphanedLoaders();
      this.getAgreementLogByClinicId();
    },
  };
</script>

<style>
  .contract-agreement-card {
    width: 100%;
  }

  @media (min-width: 992px) {
    .contract-agreement-card {
      width: 680px;
    }
  }

  .contract-agreement-card .vs-card--content {
    padding: 0px;
    margin: 0px;
    height: 100%;
  }

  .contract-agreement-content-back-btn.includeIconOnly.large {
    position: absolute;
    top: 15px;
    left: 15px;
    width: 24px !important;
    height: 24px !important;
    padding: 0px;
  }

  @media (min-width: 992px) {
    .contract-agreement-content-back-btn.includeIconOnly.large {
      top: 73px;
      left: 45px;
      width: 44px !important;
      height: 44px !important;
      padding: 12px;
    }
  }
</style>
